import { defineStore } from "pinia";
import { ref, computed } from "vue";
import client from "@/api/client";

export const useCoupleStore = defineStore("couple", () => {
  const couple = ref(null);
  const invite = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const hasCouple = computed(() => !!couple.value);
  const members = computed(() => couple.value?.active_members || couple.value?.members || []);
  const isComplete = computed(
    () => members.value.filter((m) => m.status === "active").length === 2
  );
  const currentMember = computed(() =>
    members.value.find((m) => m.is_current_user)
  );
  const partnerMember = computed(() =>
    members.value.find((m) => !m.is_current_user)
  );
  const welcomed = computed(() => !!couple.value?.welcomed);
  const bothDiagnosed = computed(() => couple.value?.both_diagnosed === true);

  async function fetchCurrent() {
    loading.value = true;
    error.value = null;
    try {
      const response = await client.get("/couples/current");
      couple.value = response.data.couple;
      invite.value = response.data.invite;
      return { success: true };
    } catch (e) {
      if (e.response?.status === 404) {
        couple.value = null;
        invite.value = null;
        return { success: true, notFound: true };
      }
      error.value = e.response?.data?.error || "Erro ao carregar dados do casal";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function create(data) {
    loading.value = true;
    error.value = null;
    try {
      const response = await client.post("/couples", { couple: data });
      couple.value = response.data.couple;
      return { success: true, couple: response.data.couple };
    } catch (e) {
      error.value =
        e.response?.data?.errors?.[0] || "Erro ao criar perfil do casal";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function createInvite({ partnerName, partnerEmail, welcomeVideo } = {}) {
    loading.value = true;
    error.value = null;
    try {
      let response;
      if (welcomeVideo) {
        const formData = new FormData();
        if (partnerName !== undefined) formData.append("invite[partner_name]", partnerName ?? "");
        if (partnerEmail !== undefined) formData.append("invite[partner_email]", partnerEmail ?? "");
        formData.append("invite[welcome_video]", welcomeVideo);
        response = await client.post(
          `/couples/${couple.value.id}/invites`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        const payload = {};
        if (partnerName !== undefined || partnerEmail !== undefined) {
          payload.invite = {
            partner_name: partnerName ?? null,
            partner_email: partnerEmail ?? null,
          };
        }
        response = await client.post(
          `/couples/${couple.value.id}/invites`,
          payload
        );
      }
      invite.value = response.data.invite;
      return { success: true, invite: response.data.invite };
    } catch (e) {
      error.value =
        e.response?.data?.errors?.[0] ||
        e.response?.data?.error ||
        "Erro ao criar convite";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function cancelInvite() {
    if (!invite.value) return { success: false };
    loading.value = true;
    error.value = null;
    try {
      await client.delete(`/invites/${invite.value.token}`);
      invite.value = null;
      return { success: true };
    } catch (e) {
      error.value = e.response?.data?.error || "Erro ao cancelar convite";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function markWelcomed() {
    if (!couple.value) return { success: false };
    try {
      const response = await client.post("/couples/welcome");
      couple.value = response.data.couple;
      return { success: true };
    } catch (e) {
      error.value = e.response?.data?.error || "Erro ao registrar boas-vindas";
      return { success: false, error: error.value };
    }
  }

  function reset() {
    couple.value = null;
    invite.value = null;
    error.value = null;
  }

  return {
    couple,
    invite,
    loading,
    error,
    hasCouple,
    isComplete,
    currentMember,
    partnerMember,
    welcomed,
    bothDiagnosed,
    fetchCurrent,
    create,
    createInvite,
    cancelInvite,
    markWelcomed,
    reset,
  };
});
